import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { AreaDeTexto, Campo } from './campos';
import { Modal } from './Modal';
import { moeda } from '../util/formato';

export interface ParcelaParaAlterar {
  readonly id: string;
  readonly numero: number;
  /** Em centavos. */
  readonly valor: number;
  readonly vencimento: string;
}

function emReais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function lerCentavos(texto: string): number | null {
  const limpo = texto.replace(/[R$\s]/g, '');
  if (!limpo) return null;
  const n = Number(limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * Alteração de uma parcela em aberto.
 *
 * Renegociar prazo e valor é rotina — o devedor liga, combina outra data, arredonda
 * o valor. O que não é rotina é a consequência: mudar o valor de uma parcela move o
 * TOTAL DO ACORDO junto, pela diferença.
 *
 * Isso não é escolha de implementação, é a regra do sistema: a criação do acordo
 * RECUSA um parcelamento cuja soma não bata com o total. A tela mostra o total novo
 * antes de confirmar, porque quem está mexendo no valor de uma parcela normalmente
 * não está pensando no acordo inteiro.
 */
export function ModalAlterarParcela({
  aberto,
  aoFechar,
  parcela,
  devedorId,
  totalDoAcordo,
}: {
  aberto: boolean;
  aoFechar: () => void;
  parcela: ParcelaParaAlterar | null;
  devedorId: string;
  /** Em centavos — para mostrar o total novo antes de confirmar. */
  totalDoAcordo: number;
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();
  const [valor, setValor] = useState('');
  const [vencimento, setVencimento] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setValor(parcela ? emReais(parcela.valor) : '');
    setVencimento(parcela?.vencimento.slice(0, 10) ?? '');
    setMotivo('');
    setErro(null);
  }, [parcela, aberto]);

  const centavos = lerCentavos(valor);
  const diferenca = parcela && centavos !== null ? centavos - parcela.valor : 0;

  const alterar = useMutation({
    mutationFn: () =>
      api.alterar(`/parcelas/${parcela?.id}`, {
        valor,
        vencimento,
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
      }),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['contagens'] });
      aoFechar();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível alterar a parcela.'),
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (centavos === null || centavos <= 0) {
      setErro('Informe o valor da parcela.');
      return;
    }
    if (!vencimento) {
      setErro('Informe o vencimento.');
      return;
    }
    alterar.mutate();
  }

  if (!parcela) return null;

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={parcela.numero === 0 ? 'Alterar a entrada' : `Alterar a parcela ${parcela.numero}`}
      descricao="Renegociação de prazo ou de valor."
      largura="30rem"
      rodape={
        <>
          <Botao onClick={aoFechar} desabilitado={alterar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={alterar.isPending}
          >
            {alterar.isPending ? 'Salvando…' : 'Salvar alteração'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-4">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Valor (R$)"
            valor={valor}
            aoMudar={(v) => {
              setValor(v);
              setErro(null);
            }}
            placeholder="125,00"
          />
          <Campo
            rotulo="Vencimento"
            tipo="date"
            valor={vencimento}
            aoMudar={setVencimento}
          />
        </div>

        {/* A consequência que ninguém espera, à vista antes de confirmar. */}
        {diferenca !== 0 && centavos !== null && (
          <Alerta tom="atencao">
            O total do acordo passa de <strong>{moeda(totalDoAcordo)}</strong> para{' '}
            <strong>{moeda(totalDoAcordo + diferenca)}</strong> — a soma das parcelas
            precisa fechar com ele.
          </Alerta>
        )}

        <AreaDeTexto
          rotulo="Motivo (opcional)"
          valor={motivo}
          aoMudar={setMotivo}
          linhas={2}
          placeholder="Ex.: renegociado por telefone em 03/09."
          auxilio="Vai para o histórico do devedor, que é onde se procura depois."
        />

        <p className="text-xs text-[color:var(--texto-tenue)]">
          A cobrança já enfileirada desta parcela é cancelada — ela foi montada com o
          valor e a data antigos. Parcela já baixada não se altera aqui: estorne a
          baixa primeiro, porque os honorários foram congelados nela.
        </p>
      </form>
    </Modal>
  );
}
